'use client';

import React, { useEffect } from 'react';
import { SignedIn, SignedOut, useUser } from '@clerk/nextjs';
import { usePathname } from 'next/navigation';
import { AnimatedLandingPage } from './components/shared/AnimatedLandingPage';
import { segmentClient } from '@/lib/analytics/segment';
import { getUserEnvironment, getViewportInfo } from '@/lib/analytics/environment';
import { initDatadogRum } from '@/lib/analytics/datadogRum';
import { initDatadogLogs } from '@/lib/analytics/datadogLogger';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import { usePendingCharacterSave } from './hooks/usePendingCharacterSave';

type ClientClerkWrapperProps = {
  isAllowedEmail?: boolean;
  children: React.ReactNode;
};

function SegmentIdentifier() {
  const { user, isLoaded } = useUser();
  const prevUserRef = React.useRef<typeof user>(undefined);

  useEffect(() => {
    if (isLoaded) {
      if (user) {
        const { id, emailAddresses, firstName, lastName, fullName, createdAt } = user;
        const email = emailAddresses[0]?.emailAddress;

        // Get browser/device environment information
        const environment = getUserEnvironment();
        const viewport = getViewportInfo();

        // Identify the user in Segment with enhanced traits
        segmentClient.identify(id, {
          email,
          firstName,
          lastName,
          fullName,
          createdAt,
          // Browser/Device traits
          browser: environment.browserName,
          browserVersion: environment.browserVersion,
          deviceType: environment.deviceType,
          os: environment.os,
          screenWidth: environment.screenWidth,
          screenHeight: environment.screenHeight,
          viewport: viewport.viewport,
          timezone: environment.timezone,
          language: environment.language,
          userAgent: environment.userAgent,
          referrer: environment.referrer,
        });
      } else {
        // Only reset if user was previously signed in (actual logout)
        if (prevUserRef.current) {
          segmentClient.reset();
        }
      }
      prevUserRef.current = user;
    }
  }, [isLoaded, user]);

  return null;
}

function PendingCharacterSaveHandler() {
  // Handle pending character save after authentication
  usePendingCharacterSave();
  return null;
}

// just a placeholder to avoid the default error boundary from Datadog
const EmptyFallback = () => null;

export default function ClientClerkWrapper({ children }: ClientClerkWrapperProps) {
  const pathname = usePathname();

  // Allow anonymous access to character creation routes, share pages, and TV/remote mode
  const isCharacterCreationRoute = pathname?.startsWith('/character_creation_room/');
  const isShareRoute = pathname?.startsWith('/share/');
  const isShowcaseRoute = pathname?.startsWith('/showcase');
  const isTVRoute = pathname?.startsWith('/tv');
  const isRemoteRoute = pathname?.startsWith('/remote');

  // track game session start/end when app is mounted/unmounted
  useEffect(() => {
    // Initialize Datadog RUM + Logs
    initDatadogRum();
    initDatadogLogs();

    // Detect if user is arriving as a room starter or room joiner
    const pathname = window.location.pathname;
    const isRoomJoiner = pathname.startsWith('/rooms/');
    const roomCode = isRoomJoiner ? pathname.split('/rooms/')[1]?.split('?')[0] : null;

    segmentClient.track('Game Session Start', {
      source: 'client',
      message: isRoomJoiner ? 'join room' : 'start room',
      roomName: roomCode || undefined,
    });

    return () => {
      segmentClient.track('Game Session End');
    };
  }, []);

  // For TV/remote routes (loaded in cross-origin iframe), render directly
  // without waiting for Clerk auth state.
  if (isTVRoute || isRemoteRoute) {
    return <ErrorBoundary fallback={EmptyFallback}>{children}</ErrorBoundary>;
  }

  return (
    <ErrorBoundary fallback={EmptyFallback}>
      {/* If not signed in, show landing page UNLESS on allowed anonymous routes */}
      <SignedOut>
        {isCharacterCreationRoute || isShareRoute || isShowcaseRoute ? (
          // Allow access to these pages without sign-in
          children
        ) : (
          <AnimatedLandingPage />
        )}
      </SignedOut>

      <SignedIn>
        {children}
        <PendingCharacterSaveHandler />
      </SignedIn>
      <SegmentIdentifier />
    </ErrorBoundary>
  );
}
